import { Dispatch, SetStateAction, useMemo } from 'react';
import { EventAccordionItemProps } from 'src/components/Accordions';
import { PurchasesSideTable } from 'src/components/Purchases/PurchaseEventPage/PurchasesSideTable';
import { SalesSideTable } from 'src/components/Sales/SaleEventPage/SaleSideTable';
import { useMatchMedia } from 'src/hooks/useMatchMedia';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { EventWithData, Feature, Listing } from 'src/WebApiController';

import { InventoryEventTabs } from '../InventoryEventTabs';
import { InventoryEventDashboardSection } from '../Sections/InventoryEventDashboardSection';
import { InventoryEventFulfillmentRulesSection } from '../Sections/InventoryEventFulfillmentRulesSection';
import { InventoryEventFulfillmentSection } from '../Sections/InventoryEventFulfillmentSection';
import { InventoryEventInfoSection } from '../Sections/InventoryEventInfoSection';
import { InventoryPricingABTestSection } from '../Sections/InventoryPricingABTestSection';
import { ManualPricingSection } from '../Sections/ManualPricingSection';
import { NoteSectionV2 } from '../Sections/NoteSectionV2';
import { TagsSection } from '../Sections/TagsSection';
import { SellThroughRateSection } from '../SellThroughRateSection';
import * as styles from './InventoryEventPage.css';

export const InventoryEventPageContent = ({
  activeTab,
  accordionItemProps,
  inventoryAccordionItem,
  selectedListingIds,
  setSelectedListingIds,
}: {
  activeTab: InventoryEventTabs;
  accordionItemProps?: EventWithData;
  inventoryAccordionItem?: EventAccordionItemProps<Listing>;
  selectedListingIds: number[];
  setSelectedListingIds: Dispatch<SetStateAction<number[]>>;
}) => {
  const hasFulfillmentRulesEventLevelFeature = useUserHasFeature(
    Feature.FulfillmentRulesEventLevel
  );
  const hasMobileInventoryEventPageFeature = useUserHasFeature(
    Feature.MobileInventoryEventPage
  );
  const isMobile = useMatchMedia('mobile');
  const useMobileInventory = useMemo(
    () => isMobile && hasMobileInventoryEventPageFeature,
    [hasMobileInventoryEventPageFeature, isMobile]
  );

  const listingIdFilterQuery = useMemo(() => {
    return {
      listingIds: selectedListingIds?.length > 0 ? selectedListingIds : null,
    };
  }, [selectedListingIds]);

  const shouldShowInventoryView = useMemo(() => {
    if (activeTab === InventoryEventTabs.Info) {
      return true;
    }
    if (useMobileInventory && activeTab === InventoryEventTabs.ManualPricing) {
      return true;
    }
    return false;
  }, [activeTab, useMobileInventory]);

  if (shouldShowInventoryView) {
    return (
      <InventoryEventInfoSection
        accordionItemProps={accordionItemProps}
        inventoryAccordionItem={inventoryAccordionItem}
        selectedListingIds={selectedListingIds}
        setSelectedListingIds={setSelectedListingIds}
        cleanMode
      />
    );
  }

  if (activeTab === InventoryEventTabs.ManualPricing) {
    return <ManualPricingSection accordionItemProps={accordionItemProps} />;
  }

  if (activeTab === InventoryEventTabs.Dashboard) {
    return (
      <InventoryEventDashboardSection accordionItemProps={accordionItemProps} />
    );
  }

  if (activeTab === InventoryEventTabs.Fulfillment) {
    return (
      <>
        <InventoryEventFulfillmentSection
          accordionItemProps={accordionItemProps}
          inventoryAccordionItem={inventoryAccordionItem}
        />
        <hr />
        {hasFulfillmentRulesEventLevelFeature && (
          <InventoryEventFulfillmentRulesSection
            accordionItemProps={accordionItemProps}
            inventoryAccordionItem={inventoryAccordionItem}
          />
        )}
      </>
    );
  }

  if (activeTab === InventoryEventTabs.PricingABTest) {
    return (
      <InventoryPricingABTestSection
        event={accordionItemProps?.event!}
        listings={accordionItemProps?.entities?.listings ?? []}
      />
    );
  }

  if (activeTab === InventoryEventTabs.SellThroughRate) {
    return <SellThroughRateSection event={accordionItemProps?.event!} />;
  }
  if (activeTab === InventoryEventTabs.Tags) {
    return <TagsSection eventId={accordionItemProps?.event!.virtId!} />;
  }

  if (useMobileInventory && activeTab === InventoryEventTabs.Mobile_Purchases) {
    return (
      <div className={styles.mobileContentContainer}>
        <PurchasesSideTable
          viagVirtId={accordionItemProps?.event!.virtId!}
          queryInput={listingIdFilterQuery}
        />
      </div>
    );
  }

  if (useMobileInventory && activeTab === InventoryEventTabs.Mobile_Notes) {
    return (
      <div className={styles.mobileContentContainer}>
        <NoteSectionV2 eventId={accordionItemProps?.event!.viagId!} />
      </div>
    );
  }

  if (useMobileInventory && activeTab === InventoryEventTabs.Mobile_Sales) {
    return (
      <div className={styles.mobileContentContainer}>
        <SalesSideTable
          viagVirtId={accordionItemProps?.event!.virtId!}
          queryInput={listingIdFilterQuery}
        />
      </div>
    );
  }

  return null;
};
